// ============================================================================
// Share my application link (TCSD-0370) — the standing per-AM link, minted at
// launch (TCSD-0373 ¶6). Text it · Copy link · Show QR code. This sheet IS the
// old Intake Links tab, moved to where Jack could find it.
// Whoever applies through it lands as a Lead in this manager's markets with the
// link recorded as where they came from (submit_sourcing_intake).
// Someone with no personal link (HR, an executive) picks a market's link.
// QR: the `qrcode` package on a <canvas>, as the Intake Links tab drew it — no external call.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { useToast } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { intakeUrl } from '../../sourcing/hooks/use-sourcing-data';
import { Sheet, bigPrimary, bigSecondary } from './sheet';
import { openText } from './actions';

interface Code {
  code: string;
  label: string | null;
  created_by: string | null;
}

export function ShareLinkSheet({ onClose }: { onClose: () => void }) {
  const { employee } = useAuth();
  const me = employee?.employee_id ?? null;
  const toast = useToast();
  const [codes, setCodes] = useState<Code[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase
        .from('sourcing_referral_codes')
        .select('code, label, created_by')
        .eq('active', true)
        .order('label');
      if (error) {
        setErr(errorMessage(error, 'Could not load the application links.'));
        return;
      }
      const rows = (data ?? []) as Code[];
      setCodes(rows);
      const mine = rows.find((r) => me && r.created_by === me);
      setChosen((mine ?? null)?.code ?? null);
    })();
  }, [me]);

  const mine = codes.find((c) => me && c.created_by === me) ?? null;
  const marketCodes = codes.filter((c) => !c.created_by && c.label?.endsWith('Metro'));
  const url = chosen ? intakeUrl(chosen.toLowerCase()) : null;
  const chosenLabel = codes.find((c) => c.code === chosen)?.label ?? null;

  useEffect(() => {
    if (showQr && url && canvasRef.current) void QRCode.toCanvas(canvasRef.current, url, { width: 240, margin: 2 });
  }, [showQr, url]);

  return (
    <Sheet title="Your application link" label="Your application link" onClose={onClose}>
      {err && <p className="rounded-md bg-st-danger-bg p-2 text-sm text-st-danger-fg">{err}</p>}
      <p className="mb-2 text-sm text-tcs-text-secondary">
        {mine && chosen === mine.code
          ? 'Anyone who fills this in lands in your pipeline as a Lead, in your markets, with your name on it.'
          : chosenLabel
            ? `Anyone who fills this in lands as a Lead in ${chosenLabel}.`
            : 'Pick a market’s link to share.'}
      </p>
      {!mine && (
        <select
          aria-label="Market link"
          value={chosen ?? ''}
          onChange={(e) => setChosen(e.target.value || null)}
          className="mb-2 w-full rounded-lg border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
        >
          <option value="">Choose a market…</option>
          {marketCodes.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      )}
      {url && (
        <>
          <div className="break-all rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2 font-mono text-xs text-tcs-text">{url}</div>
          <button type="button" className={bigPrimary} onClick={() => openText(null, `Apply to clean with Terminal Cleaning Services: ${url}`)}>
            Text it
          </button>
          <button
            type="button"
            className={bigSecondary}
            onClick={() => {
              void navigator.clipboard
                .writeText(url)
                .then(() => toast.success('Link copied'))
                .catch(() => toast.error('Could not copy — press and hold the link to copy it'));
            }}
          >
            Copy link
          </button>
          <button type="button" className={bigSecondary} onClick={() => setShowQr((v) => !v)}>
            {showQr ? 'Hide QR code' : 'Show QR code'}
          </button>
          {showQr && <canvas ref={canvasRef} aria-label="QR code for the link" className="mx-auto mt-3 rounded-lg" />}
        </>
      )}
    </Sheet>
  );
}
